import { Modal, ModalBody, ModalFooter, ModalHeader } from 'components/utility/react-modal'
import React, { useEffect, useRef, useState } from 'react'

// Submission has no server-reported progress (a single apply() call), so the bar
// eases toward 90% while the request is in flight rather than sitting static.
const SUBMIT_PROGRESS_CEILING = 90
const SUBMIT_PROGRESS_INTERVAL_MS = 200

export function ReadyToSubmitModal({
  isOpen,
  roundsOn,
  hasImmediateFees,
  immediateFeesName,
  immediateFeesDescriptor,
  attestationSubline = null,
  isSubmitting = false,
  onBack,
  onConfirm,
}) {
  const [attested, setAttested] = useState(false)
  const [progress, setProgress] = useState(0)
  const progressTimerRef = useRef(null)

  useEffect(() => {
    clearInterval(progressTimerRef.current)

    if (!isSubmitting) {
      setProgress(0)
      return
    }

    progressTimerRef.current = setInterval(() => {
      setProgress((prev) => (prev >= SUBMIT_PROGRESS_CEILING ? prev : prev + (SUBMIT_PROGRESS_CEILING - prev) * 0.1))
    }, SUBMIT_PROGRESS_INTERVAL_MS)

    return () => clearInterval(progressTimerRef.current)
  }, [isSubmitting])

  const handleClose = () => {
    setAttested(false)
    onBack()
  }

  const handleConfirm = () => {
    if (!attested) return
    onConfirm()
  }

  const listItems = []

  if (roundsOn) {
    listItems.push(
      <span key="rounds" className="dome-color-dark-grey">
        Edits will be locked while the property reviews your application.
      </span>
    )
  }

  if (hasImmediateFees) {
    listItems.push(
      <div key="fees" className="dome-d-flex dome-flex-column dome-gap3">
        <span className="dome-color-dark-grey">
          {immediateFeesName} fees will be collected and show on the card statement as:
        </span>
        <span className="dome-text-w500 dome-color-darkest-grey">{immediateFeesDescriptor}</span>
      </div>
    )
  }

  const authorizedFeesLabel = hasImmediateFees ? 'Other authorized' : 'Authorized'
  const authorizedText = [
    `${authorizedFeesLabel} fees will be collected during processing.`,
    roundsOn ? 'You could still be asked to make updates at a later stage.' : null,
  ]
    .filter(Boolean)
    .join(' ')

  listItems.push(
    <span key="authorized" className="dome-color-dark-grey">
      {authorizedText}
    </span>
  )

  const useList = listItems.length > 1

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOutsideClick={false} className="submission-modal">
      <div
        className="dome-d-flex dome-flex-column dome-gap12"
        style={{ opacity: isSubmitting ? 0.3 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}
      >
        <i className="fa-kit fa-thin-file-contract-circle-arrow-right dome-color-dark-green submission-modal-icon" />
        <span className="dome-h2 dome-color-dark-green">ready to submit?</span>
      </div>

      <ModalBody className="dome-gap18">
        <div
          className="dome-d-flex dome-flex-column dome-gap18"
          style={{ opacity: isSubmitting ? 0.3 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}
        >
          <div className="dome-d-flex dome-flex-column dome-gap12">
            {useList ? (
              <>
                <span className="dome-p1 dome-color-dark-grey">Immediately upon submission:</span>
                <ol
                  className="dome-d-flex dome-flex-column dome-gap6 dome-p1"
                  style={{ paddingLeft: 20, margin: 0, lineHeight: '20px' }}
                >
                  {listItems.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ol>
              </>
            ) : (
              listItems[0]
            )}
          </div>

          <div className="dome-d-flex dome-flex-column dome-gap6 dome-padding6 dome-bg-fill dome-rounded-border">
            <label className="dome-d-flex dome-align-start dome-gap6" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={attested}
                disabled={isSubmitting}
                onChange={(e) => setAttested(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0, accentColor: 'var(--darkest-grey)' }}
              />
              <span className="dome-p2 dome-color-darkest-grey dome-text-w500">
                This application package is complete and ready for property review.
              </span>
            </label>
            {attestationSubline && (
              <span className="dome-p3 dome-color-dark-grey submission-modal-attestation-subline">
                {attestationSubline}
              </span>
            )}
          </div>
        </div>
        {isSubmitting && (
          <div className="dome-d-flex dome-flex-column dome-gap3">
            <span className="dome-p2 dome-color-darkest-grey">submission in progress, please hang tight</span>
            <div className="dome-d-flex dome-align-center dome-gap6">
              <div style={{ height: 6, width: `100%`, background: '#DBDBDB', borderRadius: 12 }}>
                <div
                  style={{
                    height: 6,
                    width: `${progress}%`,
                    background: '#4C7E77',
                    borderRadius: 12,
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
              <span className="dome-p3 dome-color-med-grey">{Math.floor(progress)}%</span>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <button
          type="button"
          onClick={handleClose}
          disabled={isSubmitting}
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-chevron-left"
        >
          back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!attested || isSubmitting}
          className={`dome-btn dome-btn-base ${attested && !isSubmitting ? 'dome-btn-dark-green' : 'dome-btn-disabled'}`}
        >
          yes, submit
        </button>
      </ModalFooter>
    </Modal>
  )
}

export default ReadyToSubmitModal
